import { authFetch } from './auth';
import type { InterviewConfig } from '../types';
import type { SalaryMessage } from '../types/salaryNegotiation';
import type { BehaviorSummary } from '../types';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://mmbang.site';

// 谈薪结束关键词 - 会被语音播放出来
export const SALARY_END_KEYWORDS = [
  // 达成共识
  '那我们就确定',
  '那就这么定了',
  '欢迎加入',
  '期待你的加入',
  // 候选人拒绝
  '那确实有点遗憾',
  '希望下次有机会',
  '那您再考虑考虑',
  // 无法继续
  '这已经是我们能给出的最高薪资了',
  '超出我们的预算范围了',
  '实在没办法再调整了',
  '今天的沟通就先到这里',
  '薪酬沟通到此结束，谢谢你的参与',
];

// HR回应接口
export interface HRResponse {
  text: string;
  offerAmount?: number;
  isFinal: boolean;
}

// 面试维度评分
export interface InterviewDimensions {
  expression: number;
  technical: number;
  logic: number;
  communication: number;
  professionalism: number;
  learning: number;
}

// 谈薪配置
export interface SalaryNegotiationConfig {
  interviewConfig: InterviewConfig;
  overallScore: number;
  dimensions?: InterviewDimensions; // 6个维度评分
}

// 检查是否应该结束谈薪
export function shouldEndNegotiation(
  dialogueHistory: SalaryMessage[],
  currentRound: number,
): boolean {
  // 最大10轮强制结束
  if (currentRound >= 10) return true;

  // 检查HR最后一条回复是否包含结束关键词
  const lastHRMessage = dialogueHistory.filter((m) => m.role === 'hr').pop();

  if (lastHRMessage) {
    const content = lastHRMessage.content;
    const hasEndKeyword = SALARY_END_KEYWORDS.some((keyword) =>
      content.includes(keyword),
    );
    return hasEndKeyword;
  }

  return false;
}

// 从文本中提取薪资数字
function extractSalaryAmount(text: string): number | undefined {
  const match = text.match(/(\d+)(?:k|K|千)/i);
  if (match) {
    return parseInt(match[1], 10);
  }
  return undefined;
}

// 生成HR回应
export async function generateHRResponse(
  config: SalaryNegotiationConfig,
  dialogueHistory: SalaryMessage[],
  currentRound: number,
): Promise<HRResponse> {
  try {
    const response = await authFetch(`${API_BASE_URL}/api/salary-negotiation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        overallScore: config.overallScore,
        currentRound,
        dialogueHistory,
        jobType: config.interviewConfig.jobType,
        jobLevel: config.interviewConfig.level,
        dimensions: config.dimensions, // 传递6个维度评分
      }),
    });

    if (!response.ok) {
      throw new Error('生成HR回应失败');
    }

    const data = await response.json();
    const text = data.content || '';

    // 提取offer金额
    const offerAmount = extractSalaryAmount(text);

    // 检查是否是最终offer
    const isFinal = SALARY_END_KEYWORDS.some((keyword) =>
      text.includes(keyword),
    );

    return {
      text,
      offerAmount,
      isFinal,
    };
  } catch {
    // 返回默认回应
    return getDefaultHRResponse(currentRound);
  }
}

// 默认HR回应（当API失败时使用）
function getDefaultHRResponse(currentRound: number): HRResponse {
  const defaultResponses: Record<number, HRResponse> = {
    1: {
      text: '我们了解到您的期望了。根据公司的薪酬体系，这个岗位目前的预算范围有限，您看能不能稍微降低一点期望？',
      isFinal: false,
    },
    2: {
      text: '除了基本工资之外，我们年终奖有3个月，还有补充医疗保险，这些福利加起来也是很可观的。',
      isFinal: false,
    },
    3: {
      text: '那我们就确定30k，加上年终奖和福利，总包还是很有竞争力的。欢迎您的加入！',
      offerAmount: 30,
      isFinal: true,
    },
  };

  return (
    defaultResponses[currentRound] || {
      text: '好的，那您再考虑考虑，有决定后随时联系我。',
      isFinal: true,
    }
  );
}

// 获取谈薪开场白 - 复用 generateHRResponse，不传 dialogueHistory
export async function generateSalaryOpening(
  config: SalaryNegotiationConfig,
): Promise<string> {
  try {
    // 调用 generateHRResponse，不传 dialogueHistory，让后端识别为开场白请求
    const response = await generateHRResponse(config, [], 1);
    return response.text;
  } catch {
    // 返回默认开场白
    return getDefaultOpening(config.overallScore);
  }
}

// 默认开场白
function getDefaultOpening(overallScore: number): string {
  if (overallScore >= 90) {
    return `您好！首先恭喜您通过面试，技术团队对您的表现评价非常高。我是负责薪酬沟通的HR总监，想跟您聊聊薪资方面的事情。方便告诉我您目前的薪资情况吗？比如月薪总包大概是多少？`;
  } else if (overallScore >= 75) {
    return `您好，恭喜您通过面试！我是HR总监，想跟您沟通一下薪酬方面的事。首先想了解一下，您目前的薪资水平大概是怎样的？`;
  } else {
    return `您好，面试流程已经结束了。我是HR总监，想跟您沟通一下薪资。首先请问您目前的月薪是多少？`;
  }
}

// 生成谈薪报告
export async function generateSalaryReport(
  config: SalaryNegotiationConfig,
  dialogueHistory: SalaryMessage[],
  finalOffer?: number,
  callDuration?: number,
  behaviorSummary?: BehaviorSummary,
) {
  try {
    const response = await authFetch(`${API_BASE_URL}/api/salary-report`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        overallScore: config.overallScore,
        finalOffer,
        dialogueHistory,
        jobType: config.interviewConfig.jobType,
        jobLevel: config.interviewConfig.level,
        callDuration, // 传递谈薪时长
        dimensions: config.dimensions, // 传递面试维度评分
        behaviorSummary: behaviorSummary
          ? (({ timeline, duration, ...rest }) => rest)(behaviorSummary)
          : undefined, // 传递行为分析数据（不含时间线和时长）
      }),
    });

    if (!response.ok) {
      throw new Error('生成报告失败');
    }

    return await response.json();
  } catch {
    // 返回默认报告
    return getDefaultReport(dialogueHistory, finalOffer);
  }
}

// 默认报告
function getDefaultReport(
  dialogueHistory: SalaryMessage[],
  finalOffer?: number,
) {
  const totalRounds = dialogueHistory.filter((m) => m.role === 'hr').length;

  return {
    result: {
      finalOffer: finalOffer || 0,
      isAccepted: !!finalOffer,
      totalRounds,
      userPerformance: {
        strategy: 75,
        communication: 80,
        timing: 70,
        flexibility: 75,
      },
    },
    analysis: {
      strengths: ['能够清晰表达薪资期望', '沟通态度积极'],
      weaknesses: ['让步较快', '未充分利用面试优势'],
      keyMoments: [
        {
          round: 1,
          description: '信息采集阶段',
          suggestion: '提前准备好薪资数据会更有利',
        },
      ],
    },
    suggestions: [
      {
        category: '谈判策略',
        content: '建议先询问对方预算范围，不要先暴露底线',
      },
    ],
    marketComparison: {
      userExpected: 0,
      marketMedian: 35,
      finalOffer: finalOffer || 0,
      percentile: 60,
    },
  };
}
